/**
 * Reassign a lineage member's refs to another revision, ONE brand-scoped AKA at
 * a time (memo 5.5 + the ETA 2824 refinement).
 *
 * Works on ANY member of a line, not only the placeholder: the bare 2824 is a
 * real 1971–79 movement, so its pre-1979 refs legitimately stay while misfiled
 * later ones move by evidence. The evidence is the AKA the ref came in under,
 * shown beside every ref, and kept: the ref's calibre TEXT is never rewritten
 * (the push runs with legacyCalibreString off).
 *
 * Refs matched only by an un-branded alias, or by none, are not offered — they
 * stay on this record as the fallback. No automatic bulk reassignment: every
 * move is a curator's pick of brand, refs and target.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import {
  getReferencesByDocIds,
  recomputeCalibreReferenceCountsFor,
  type CustomCalibreEntry,
} from '../../services/firebase';
import { loadBrandAkaGroups, moveBrandAlias } from '../../services/calibreLineage';
import { sortMembers, type BrandAkaGroup } from '../../services/calibreLineageLogic';
import { pushCalibreDataToRefs } from './parentCalibrePush';
import type { WatchReference } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  calibre: CustomCalibreEntry;
  /** The other members of the calibre's line — the reassignment targets. */
  siblings: CustomCalibreEntry[];
  onReassigned?: () => void;
}

interface GroupState {
  refs: Map<string, WatchReference> | null;
  selected: Set<string>;
  targetId: string;
  moveAlias: boolean;
}

export function LineageReassignDialog({ isOpen, onClose, calibre, siblings, onReassigned }: Props) {
  const legacyMovement = useFeatureFlag('calibre.dualWriteLegacyMovement');
  const [groups, setGroups] = useState<BrandAkaGroup[] | null>(null);
  const [fallbackCount, setFallbackCount] = useState(0);
  const [state, setState] = useState<Record<string, GroupState>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const targets = useMemo(() => sortMembers(siblings.filter((s) => s.id !== calibre.id)), [siblings, calibre.id]);

  const keyOf = (g: BrandAkaGroup) => `${g.brandId}|${g.akaName}`;

  const load = useCallback(async () => {
    setGroups(null);
    try {
      const { groups: gs, docs, fallbackCount: fb } = await loadBrandAkaGroups(calibre);
      setFallbackCount(fb);
      setGroups(gs);
      setState(
        Object.fromEntries(
          gs.map((g) => [
            keyOf(g),
            {
              refs: new Map(g.refs.flatMap((r) => {
                const d = docs.get(`${r.brandId}/${r.refId}`);
                return d ? [[r.refId, d] as const] : [];
              })),
              selected: new Set(g.refs.map((r) => r.refId)),
              targetId: '',
              moveAlias: true,
            },
          ]),
        ),
      );
    } catch (err) {
      console.error('Failed to load linked references', err);
      toast.error(err instanceof Error ? err.message : 'Could not load references');
      setGroups([]);
    }
  }, [calibre]);

  useEffect(() => {
    if (isOpen) void load();
  }, [isOpen, load]);

  const patch = (key: string, next: Partial<GroupState>) =>
    setState((prev) => ({ ...prev, [key]: { ...prev[key], ...next } }));

  const handleView = (g: BrandAkaGroup) => {
    const key = keyOf(g);
    setOpen(open === key ? null : key);
  };

  const handleReassign = async (g: BrandAkaGroup) => {
    const key = keyOf(g);
    const s = state[key];
    const target = targets.find((t) => t.id === s.targetId);
    if (!target || s.selected.size === 0) return;
    setBusy(key);
    try {
      const fresh = await getReferencesByDocIds(g.brandId, [...s.selected]);
      // Only refs still on THIS calibre move — a ref relinked since Find is skipped.
      const refs = [...fresh.values()].filter((r) => r.calibreId === calibre.id);
      const runToken = `lineage-reassign-${crypto.randomUUID()}`;
      const { updatedCount } = await pushCalibreDataToRefs(
        g.brandId,
        refs,
        target,
        // The calibre TEXT is the AKA evidence: never rewritten by a reassignment.
        { legacyMovement, legacyCalibreString: false },
        runToken,
        { deferCalibreRecount: true },
      );
      if (s.moveAlias) {
        await moveBrandAlias({ fromId: calibre.id, toId: target.id, akaName: g.akaName, brandId: g.brandId });
      }
      try {
        await recomputeCalibreReferenceCountsFor([calibre.id, target.id]);
      } catch (err) {
        console.warn('Reassigned, but the reference recount failed', err);
      }
      toast.success(`Moved ${updatedCount} reference(s) to ${target.name}`);
      onReassigned?.();
      await load();
    } catch (err) {
      console.error('Reassignment failed', err);
      toast.error(err instanceof Error ? err.message : 'Reassignment failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Reassign references — ${calibre.name}`} size="xl">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          References on this calibre, grouped by the brand-scoped alias they came in under. Move a brand&rsquo;s refs
          to the revision their evidence points at. Refs matched only by an un-branded alias stay here
          {groups ? ` (${fallbackCount})` : ''}. A ref&rsquo;s calibre text is kept as the evidence trail.
        </p>
        {targets.length === 0 && (
          <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            This line has no other revision to move references to yet.
          </p>
        )}
        {groups === null ? (
          <p className="text-sm text-gray-500">Loading references…</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-gray-500">This calibre has no brand-scoped aliases.</p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {groups.map((g) => {
              const key = keyOf(g);
              const s = state[key];
              if (!s) return null;
              return (
                <li key={key} className="p-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-medium text-gray-900">{g.brandId}</span>
                    <span className="font-mono text-xs text-gray-600">AKA “{g.akaName}”</span>
                    <span className="text-xs text-gray-500">{g.refs.length} ref(s)</span>
                    {g.refs.length > 0 && (
                      <Button size="sm" variant="ghost" onClick={() => handleView(g)}>
                        {open === key ? 'Hide refs' : 'View refs'}
                      </Button>
                    )}
                  </div>
                  {open === key && (
                    <div className="max-h-64 overflow-y-auto rounded border border-gray-100">
                      {!s.refs ? (
                        <p className="p-2 text-xs text-gray-500">Loading…</p>
                      ) : (
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 text-gray-500">
                            <tr>
                              <th className="p-1.5 w-6" />
                              <th className="p-1.5 text-left font-medium">Reference</th>
                              <th className="p-1.5 text-left font-medium">Year</th>
                              <th className="p-1.5 text-left font-medium">Calibre text (evidence)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.refs.map((r) => {
                              const ref = s.refs?.get(r.refId);
                              return (
                                <tr key={r.refId} className="border-t border-gray-100">
                                  <td className="p-1.5">
                                    <input
                                      type="checkbox"
                                      aria-label={`Select ${ref?.reference ?? r.refId}`}
                                      checked={s.selected.has(r.refId)}
                                      onChange={(e) => {
                                        const next = new Set(s.selected);
                                        if (e.target.checked) next.add(r.refId);
                                        else next.delete(r.refId);
                                        patch(key, { selected: next });
                                      }}
                                    />
                                  </td>
                                  <td className="p-1.5 font-mono text-gray-900">
                                    {ref?.reference ?? r.refId}
                                    {r.isVariant && <span className="ml-1 text-gray-400">(variant)</span>}
                                  </td>
                                  <td className="p-1.5 text-gray-600">{ref?.production?.releaseYear ?? ref?.production?.startYear ?? '—'}</td>
                                  <td className="p-1.5 font-mono text-gray-600">{r.calibre}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                  {g.refs.length > 0 && (
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="text-xs text-gray-600">
                        Reassign to{' '}
                        <select
                          value={s.targetId}
                          onChange={(e) => patch(key, { targetId: e.target.value })}
                          className="ml-1 rounded border-gray-300 text-xs py-0.5"
                        >
                          <option value="">— pick a revision —</option>
                          {targets.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                              {typeof t.activeFromYear === 'number'
                                ? ` (${t.activeFromYear}–${t.activeUntilYear ?? ''})`
                                : ''}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="flex items-center gap-1 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={s.moveAlias}
                          onChange={(e) => patch(key, { moveAlias: e.target.checked })}
                        />
                        Move the AKA too
                      </label>
                      <Button
                        size="sm"
                        onClick={() => void handleReassign(g)}
                        loading={busy === key}
                        disabled={!s.targetId || s.selected.size === 0 || busy !== null}
                      >
                        Reassign {s.selected.size} ref(s)
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex justify-end pt-2 border-t">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
